import { Avatar, Box, Button, Card, Icon, Text } from '@factorialco/deprecated-design-system'
import { PerformanceProcessAgreementListCardFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

const AvatarWithCheck: React.FC<{
  signed: boolean
  name: string
  avatar?: string | null
}> = ({ signed, name, avatar }) => {
  return (
    <Avatar
      badge={{
        icon: signed ? 'check' : undefined,
        backgroundColor: 'lime1000',
        color: 'grey000White',
      }}
      label={name}
      url={buildUrl(avatar)}
      size="s32"
    />
  )
}

export const AgreementListCard: React.FC<{
  target: PerformanceProcessAgreementListCardFragment
  onInitiate?: () => Promise<unknown> | void
}> = ({ target, onInitiate = () => null }) => {
  const { navigation } = useNavigation()
  const { access: currentAccess } = useCurrentSession()

  const process = target?.performanceReviewProcess

  const agreementPath = (agreementId: string) =>
    navigation.performanceProcess.reviews.review.target.agreement.fullPath({
      targetId: target.id,
      reviewId: process?.id ?? '',
      agreementId,
    })

  const agreement = target?.agreement || null
  const reviewerFullName = agreement?.reviewer?.fullName || currentAccess?.fullName
  const reviewerAvatar = agreement?.reviewer?.avatar?.url || currentAccess?.avatar
  const targetAccess = target.access

  let signatureCount = 0
  if (agreement?.managerSignedAt) signatureCount += 1
  if (agreement?.targetSignedAt) signatureCount += 1

  const hasAgreementCreated = agreement !== null
  const canInitiateAgreement = !hasAgreementCreated && target._policies?.initiate?.can

  return (
    <Card
      to={hasAgreementCreated ? agreementPath(agreement?.id) : undefined}
      onClick={canInitiateAgreement ? async () => await onInitiate() : undefined}
    >
      <Card.Content>
        <Box gap="s12" width="full">
          <Box flexDirection="row" gap="s8">
            {targetAccess && (
              <AvatarWithCheck
                name={targetAccess.fullName}
                avatar={targetAccess.avatar}
                signed={Boolean(agreement?.targetSignedAt)}
              />
            )}
            <Box alignItems="center" justifyContent="center">
              <Icon.Tiny icon="chevron-left" />
              <Icon.Tiny icon="chevron-right" />
            </Box>

            <AvatarWithCheck
              name={reviewerFullName}
              avatar={reviewerAvatar}
              signed={Boolean(agreement?.managerSignedAt)}
            />
          </Box>
          <Text.Header2 ellipsis>{targetAccess?.fullName}</Text.Header2>
          {agreement && (
            <Text.Normal>
              <Text.Normal>
                {i18n.t('performance_process.agreements.agreement_list_card.count', {
                  count: signatureCount,
                  total: 2,
                })}
              </Text.Normal>
            </Text.Normal>
          )}
          {canInitiateAgreement && (
            <Button type="secondary" onClick={async () => await onInitiate()}>
              {i18n.t('performance_process.agreements.agreement_list_card.initiate')}
            </Button>
          )}
        </Box>
      </Card.Content>
      {hasAgreementCreated && (
        <Card.Link to={agreementPath(agreement?.id)}>
          {i18n.t('performance_process.agreements.agreement_list_card.view')}
        </Card.Link>
      )}
    </Card>
  )
}
